/**
 * audio/ambient.js — the layered procedural ambience beds.
 *
 * GDD 11.1 pillar "Carnival atmosphere": crowd murmur, distant music, rides and
 * honking should make the midway feel occupied before the player does anything.
 * None of it is recorded. The bed is:
 *
 *   - a crowd murmur — pink noise through two slowly drifting formant filters,
 *     which is what turns hiss into the impression of many distant voices;
 *   - the music, tapped through a delay and a lowpass so it reads as a second
 *     stage further down the midway rather than a copy of the near one;
 *   - scattered one-shots on random timers: ride whooshes, distant honks and
 *     short vocal blips.
 *
 * The personal space swaps all of that for aquarium bubbles and a quiet pad.
 * Scene changes crossfade rather than cut.
 *
 * @module audio/ambient
 */

import {
  noiseBuffer, filter, lfo, out, arand, arange,
} from './synth.js';
import {
  busNode, context, onTick, whenUnlocked, logPlay, LOG_ONLY, reportError,
} from './context.js';
import * as music from './music.js';

/** Seconds a scene change takes. */
const CROSSFADE = 1.4;

/** Scene name → what the bed is made of. */
const SCENES = Object.freeze({
  hub: {
    crowd: 0.85, rumble: 0.5, distantMusic: 0.5, track: 'hub-day',
    rides: [9, 18], honks: [7, 16], blips: [3, 9], bubbles: null,
  },
  stall: {
    // Inside a booth the midway is still there, just muffled and behind you.
    crowd: 0.45, rumble: 0.35, distantMusic: 0.28, track: null,
    rides: [14, 26], honks: [12, 24], blips: [6, 14], bubbles: null,
  },
  'personal-space': {
    crowd: 0, rumble: 0.18, distantMusic: 0, track: 'space-calm',
    rides: null, honks: null, blips: null, bubbles: [2.5, 6],
  },
  none: {
    crowd: 0, rumble: 0, distantMusic: 0, track: null,
    rides: null, honks: null, blips: null, bubbles: null,
  },
});

const ALIASES = Object.freeze({
  midway: 'hub',
  carnival: 'hub',
  'ambient-carnival': 'hub',
  outdoor: 'hub',
  booth: 'stall',
  space: 'personal-space',
  home: 'personal-space',
  aquarium: 'personal-space',
  off: 'none',
  silence: 'none',
});

const state = {
  /** @type {string|null} */
  scene: null,
  /** @type {object|null} */
  bed: null,
  offTick: null,
  lastTick: 0,
  /** @type {((name: string, opts: object) => void)|null} */
  play: null,
};

/** @param {string} name @returns {string|null} */
export function resolveScene(name) {
  if (!name) return null;
  const n = String(name).toLowerCase();
  if (SCENES[n]) return n;
  const a = ALIASES[n];
  return a && SCENES[a] ? a : null;
}

/** @returns {string[]} */
export function sceneNames() {
  return Object.keys(SCENES);
}

// ─────────────────────────────────────────────────────────────────────────────
// Bed construction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the continuous layers of a scene.
 * @private
 * @returns {object|null}
 */
function buildBed(sceneName) {
  const ctx = context();
  const dest = busNode('ambient');
  if (!ctx || !dest) return null;

  const cfg = SCENES[sceneName];
  const t0 = ctx.currentTime;
  const gain = ctx.createGain();
  gain.gain.value = 0.0001;
  gain.connect(dest);

  const nodes = [];
  const stopAt = [];

  if (cfg.crowd > 0) {
    // Two formants over one noise source: the cheapest thing that reads as a
    // room full of people rather than a waterfall.
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 'pink', 4);
    src.loop = true;

    for (const [freq, q, amt, rate, depth] of [
      [430, 1.3, 0.55, 0.071, 130],
      [1180, 1.8, 0.32, 0.048, 290],
    ]) {
      const f = filter(ctx, 'bandpass', freq, q);
      const g = ctx.createGain();
      g.gain.value = amt * cfg.crowd;
      src.connect(f);
      f.connect(g);
      g.connect(gain);
      nodes.push(lfo(ctx, f.frequency, { rate, depth, t0 }));
    }
    src.start(t0, arand() * 2);
    stopAt.push(src);
  }

  if (cfg.rumble > 0) {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 'brown', 4);
    src.loop = true;
    const lp = filter(ctx, 'lowpass', 190, 0.8);
    const g = ctx.createGain();
    g.gain.value = 0.16 * cfg.rumble;
    src.connect(lp);
    lp.connect(g);
    g.connect(gain);
    src.start(t0, arand() * 2);
    stopAt.push(src);
  }

  let tap = null;
  if (cfg.distantMusic > 0) {
    // Delay + lowpass = a stage further away, not a louder copy of this one.
    const delay = ctx.createDelay(0.5);
    delay.delayTime.value = 0.13;
    const lp = filter(ctx, 'lowpass', 620, 0.7);
    const g = ctx.createGain();
    g.gain.value = 0.5 * cfg.distantMusic;
    delay.connect(lp);
    lp.connect(g);
    g.connect(gain);
    tap = delay;
    music.setDistantTap(delay);
  }

  gain.gain.linearRampToValueAtTime(1, t0 + CROSSFADE);

  return {
    scene: sceneName,
    cfg,
    gain,
    nodes,
    stopAt,
    tap,
    timers: {
      rides: nextIn(cfg.rides),
      honks: nextIn(cfg.honks),
      blips: nextIn(cfg.blips),
      bubbles: nextIn(cfg.bubbles),
    },
  };
}

/** @private @param {number[]|null} range */
function nextIn(range) {
  return range ? range[0] + arand() * (range[1] - range[0]) : Infinity;
}

/** @private Fade a bed out and release its nodes. */
function teardown(bed, seconds = CROSSFADE) {
  const ctx = context();
  if (!ctx || !bed) return;
  const t = ctx.currentTime;
  bed.gain.gain.cancelScheduledValues(t);
  bed.gain.gain.setValueAtTime(Math.max(0.0001, bed.gain.gain.value), t);
  bed.gain.gain.linearRampToValueAtTime(0.0001, t + seconds);

  const end = t + seconds + 0.1;
  for (const n of bed.stopAt) { try { n.stop(end); } catch { /* already stopped */ } }
  for (const n of bed.nodes) { try { n.stop(end); } catch { /* already stopped */ } }
  if (bed.tap) music.setDistantTap(null);
  setTimeout(() => { try { bed.gain.disconnect(); } catch { /* gone */ } }, seconds * 1000 + 400);
}

// ─────────────────────────────────────────────────────────────────────────────
// Scattered one-shots
// ─────────────────────────────────────────────────────────────────────────────

/** @private A ride passing somewhere off to the side. */
function rideWhoosh() {
  const ctx = context();
  const bed = state.bed;
  if (!ctx || !bed) return;
  const pan = arange(-0.85, 0.85);
  const t0 = ctx.currentTime;
  const g = out(ctx, bed.gain, pan);
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 'pink', 4);
  src.loop = true;
  const bp = filter(ctx, 'bandpass', 300, 1.1);
  bp.frequency.setValueAtTime(240, t0);
  bp.frequency.exponentialRampToValueAtTime(1500, t0 + 0.9);
  bp.frequency.exponentialRampToValueAtTime(220, t0 + 2.2);
  src.connect(bp);
  bp.connect(g);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(0.09, t0 + 0.8);
  g.gain.linearRampToValueAtTime(0.0001, t0 + 2.2);
  src.start(t0, arand() * 2);
  src.stop(t0 + 2.4);
}

/** @private Someone else's goose, a long way off. */
function distantHonk() {
  if (!state.play) return;
  state.play('honk', {
    pan: arange(-0.9, 0.9),
    volume: arange(0.05, 0.13),
    pitch: arange(0.82, 1.2),
    bus: 'ambient',
    _ambient: true,
  });
}

/** @private A shout or a laugh in the crowd — pitched noise, not words. */
function crowdBlip() {
  const ctx = context();
  const bed = state.bed;
  if (!ctx || !bed) return;
  const t0 = ctx.currentTime;
  const g = out(ctx, bed.gain, arange(-0.9, 0.9));
  const o = ctx.createOscillator();
  o.type = 'sawtooth';
  const f = arange(220, 620);
  o.frequency.setValueAtTime(f, t0);
  o.frequency.exponentialRampToValueAtTime(f * arange(0.7, 1.5), t0 + 0.22);
  const bp = filter(ctx, 'bandpass', arange(700, 1600), 3);
  o.connect(bp);
  bp.connect(g);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(arange(0.015, 0.04), t0 + 0.06);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
  o.start(t0);
  o.stop(t0 + 0.34);
}

/** @private */
function aquariumBubbles() {
  if (!state.play) return;
  state.play('bubbles', {
    count: 2 + Math.floor(arand() * 3),
    spread: 1.2,
    pan: arange(-0.5, 0.5),
    volume: arange(0.25, 0.5),
    bus: 'ambient',
    _ambient: true,
  });
}

/** @private Advance the random-event timers. */
function tick() {
  const ctx = context();
  const bed = state.bed;
  if (!ctx || !bed) return;
  const dt = state.lastTick ? Math.max(0, ctx.currentTime - state.lastTick) : 0;
  state.lastTick = ctx.currentTime;
  if (dt <= 0) return;

  const t = bed.timers;
  const cfg = bed.cfg;
  t.rides -= dt;
  t.honks -= dt;
  t.blips -= dt;
  t.bubbles -= dt;

  if (t.rides <= 0) { rideWhoosh(); t.rides = nextIn(cfg.rides); }
  if (t.honks <= 0) { distantHonk(); t.honks = nextIn(cfg.honks); }
  if (t.blips <= 0) { crowdBlip(); t.blips = nextIn(cfg.blips); }
  if (t.bubbles <= 0) { aquariumBubbles(); t.bubbles = nextIn(cfg.bubbles); }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public surface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Start (or crossfade to) a scene's ambience.
 * @param {string} sceneName 'hub' | 'stall' | 'personal-space' | 'none'
 */
export function start(sceneName) {
  const scene = resolveScene(sceneName) || 'hub';
  if (state.scene === scene) return;
  state.scene = scene;
  logPlay('@ambient', { scene });
  if (LOG_ONLY) {
    const track = SCENES[scene].track;
    if (track) music.play(track);
    return;
  }

  whenUnlocked(() => {
    try {
      if (state.bed) teardown(state.bed);
      state.bed = buildBed(scene);
      state.lastTick = 0;
      if (!state.offTick) state.offTick = onTick(tick);

      const track = SCENES[scene].track;
      if (track) music.play(track);
    } catch (err) {
      reportError(err);
    }
  });
}

/** Fade the bed out entirely. */
export function stop() {
  logPlay('@ambient-stop', {});
  state.scene = null;
  if (LOG_ONLY) return;
  if (state.bed) {
    teardown(state.bed);
    state.bed = null;
  }
}

/**
 * Give the bed a way to fire recipes (distant honks, bubbles).
 * @param {(name: string, opts: object) => void} play
 */
export function setPlayFn(play) {
  state.play = play;
}

/** @returns {{ scene: string|null, active: boolean, scenes: string[] }} */
export function snapshot() {
  return {
    scene: state.scene,
    active: !!state.bed,
    scenes: sceneNames(),
  };
}

/** Tear everything down. Tests only. */
export function dispose() {
  if (state.offTick) { state.offTick(); state.offTick = null; }
  state.bed = null;
  state.scene = null;
}
